import styled from 'styled-components';
import React from 'react';
import { Article } from 'src/services/articles';
import { ArticleHeading } from '@shapegames/web-library-quick-draw-ui/dist/ArticleHeading';
import Image from 'next/legacy/image';
import formatArticleDate from 'src/utils/format-article-date';
import { ArticleMeta } from '@shapegames/web-library-quick-draw-ui/dist/ArticleMeta';
import FormattedFAQ from 'src/components/Formatted/FormattedFAQ';
import YoutubeEmbed from '../../../components/YoutubeEmbed';
import { imageUrlMaker, mapCropsToImageSizes } from 'src/utils/image-helpers';
import InnerHTML from 'dangerously-set-html-content';
import draftToHtml from 'draftjs-to-html';
import { mapArticleContentToDraftJSFormat } from './helpers';
import { estimateReadTime } from '../../../utils/estimate-read-time';
import ArticleShare from '../../../components/ArticleShare';
import TagBar from 'src/components/TagBar/TagBar';
import BreadCrumbs from 'src/components/BreadCrumbs';

interface ArticleDetailProps {
  article: Article;
  className?: string;
}

function ArticleDetail({ article, className }: ArticleDetailProps) {
  const crop = article.landscapeWideCrop;

  const imageUrl =
    article.image?.url &&
    imageUrlMaker({
      imageUrl: article.image.url,
      cropType: 'landscapeCoverCrop',
      crop,
    });

  const { height, width } = mapCropsToImageSizes['landscapeCoverCrop'];

  const youtubeVideoId = article.videoUrl?.split('youtu.be/')[1];

  const leadHTML = draftToHtml(
    mapArticleContentToDraftJSFormat(article.leadText),
  );
  const articleHTML = draftToHtml(
    mapArticleContentToDraftJSFormat(article.body),
  );

  const estimatedReadTime = estimateReadTime(leadHTML + '' + articleHTML);

  // TODO: Fix issue on text to return font-familiy
  const articleHTMLTempStyleFix = articleHTML.replace(
    /(font-family):([^;]*);/g,
    '',
  );
  const leadHTMLTempStyleFix = leadHTML.replace(/(font-family):([^;]*);/g, '');

  return (
    <>
      <BreadCrumbs
        currentSlug={article.slug}
        paths={[
          {
            label: article.mainArticleGroup.name,
            slug: article.mainArticleGroup.slug,
          },
          {
            label: article.title,
            slug: article.slug,
          },
        ]}
      />
      <ArticleDetailCss className={className}>
        <ArticlesDetailHeaderCss>
          <ArticleHeading>{article.title}</ArticleHeading>
        </ArticlesDetailHeaderCss>
        <TagBar tags={article.tags} />
        <ArticleMeta
          displayAuthorIcon
          authorName={article.author?.name}
          infos={[formatArticleDate(article.createdAt)]}
          size="large"
          readTime={estimatedReadTime}
        />
        {youtubeVideoId && <YoutubeEmbed videoId={youtubeVideoId} />}
        {imageUrl && !youtubeVideoId && (
          <>
            <Image
              key={`article-${article.slug}-image`}
              src={imageUrl}
              width={width}
              height={height}
              layout="responsive"
              alt={article.image?.alt}
            />
            {article.image?.caption && (
              <CaptionCss>{article.image?.caption}</CaptionCss>
            )}
          </>
        )}
        <ArticleCss>
          {article.leadText && typeof window !== 'undefined' ? (
            <InnerHTML html={leadHTMLTempStyleFix} />
          ) : (
            <div dangerouslySetInnerHTML={{ __html: leadHTMLTempStyleFix }} />
          )}
          {article.body && typeof window !== 'undefined' ? (
            <InnerHTML html={articleHTMLTempStyleFix} />
          ) : (
            <div
              dangerouslySetInnerHTML={{ __html: articleHTMLTempStyleFix }}
            />
          )}
        </ArticleCss>

        {article.htmlSnippet && <InnerHTML html={article.htmlSnippet} />}

        <ArticleShare articleTitle={article.title} />

        <FormattedFAQ inputFAQ={article.faq} />
      </ArticleDetailCss>
    </>
  );
}
const ArticleCss = styled.div`
  p {
    ${p => p.theme.getFontStyle('primary', 'medium')}
    font-size: ${p => p.theme.typography.textBase};
    line-height: 1.63;
    color: ${p => p.theme.colors.black};
    opacity: 0.8;
    word-break: break-word;
  }

  strong {
    ${p => p.theme.getFontStyle('primary', 'bold')}
  }

  h2 {
    font-size: 20px;
    margin-top: 25px;
  }

  img,
  iframe {
    max-width: 100%;
  }

  a {
    color: ${p => p.theme.colors.primary500};
    text-decoration: underline;
  }
`;

const ArticlesDetailHeaderCss = styled('div')`
  display: flex;
  justify-content: space-between;
  align-items: top;
  margin-bottom: ${p => p.theme.sizes.spacing5} !important;

  ${p => p.theme.breakpoints.maxSm} {
    display: block;
  }
`;

const ArticleDetailCss = styled('div')`
  display: flex;
  flex-direction: column;

  & > div:not(:last-child) {
    margin-bottom: ${p => p.theme.sizes.spacing5};
  }

  a {
    color: ${p => p.theme.colors.primary500};
  }

  ul,
  ol {
    padding-left: ${p => p.theme.sizes.spacing7};
  }
`;

const CaptionCss = styled.figcaption`
  padding-top: ${p => p.theme.sizes.spacing1};
  text-align: center;
  font-size: ${p => p.theme.typography.textXxs};
`;

export default ArticleDetail;
